import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { CiRedo } from "react-icons/ci";
import {
    Table,
    Card,
    Space,
    Row,
    Col,
    Tag,
    Spin,
    Button,
    message,
    Checkbox,
    Typography,
    Slider,
    ConfigProvider,
    Modal,
    Image,
    Input,
    InputNumber,
    Dropdown,
    Tooltip,
} from 'antd';
import {
    BarcodeOutlined, CaretDownOutlined,
    CloudDownloadOutlined, CodeSandboxOutlined,
    CopyOutlined,
    DeliveredProcedureOutlined, DownCircleOutlined,
    PrinterOutlined,
    ReloadOutlined, SyncOutlined,
    DownloadOutlined,
    SettingOutlined,
    PictureOutlined,
    LinkOutlined,
    SearchOutlined,
    ExclamationCircleFilled,
    CheckCircleFilled,
    ExportOutlined,
    ExperimentOutlined,
} from "@ant-design/icons";
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import ozonOrdersStore from '../../stores/OzonOrdersStore';
import { API_URL } from '../../config';
import connectSound from '../../Sound/Connect.mp3';
import disconnectSound from '../../Sound/Disconnect.mp3';
import axios from 'axios';
import Que2025 from "../Que2025/Que2025";
import Rewiews2025 from "../Rewiews2025/Rewiews2025";
import Vozvraty20203 from "../Vozvraty20203/Vozvraty20203";
import OzonPriceDetailsModal from './OzonPriceDetailsModal';
import { productEconomics } from '../Cost/costCalc';
import { applyNameReplacements, productDisplayName } from '../../utils/nameReplacements';
import dayjs from 'dayjs';
import 'dayjs/locale/ru'; // подключаем русский язык
// Добавляем стили
import './OzoneOrders.css';

const { Text } = Typography;
const statusColors = {
    awaiting_packaging: "#f50",
    awaiting_deliver: "#87d068",
    delivering: 'purple',
    delivered: 'green',
    cancelled: 'red'
};

const OZON_TABLE_SETTINGS_KEY = 'ozonOrdersTableSettings';

const TABLE_COLUMN_OPTIONS = [
    { key: 'in_process_at', label: 'Дата обработки' },
    { key: 'status', label: 'Статус' },
    { key: 'platform', label: 'Магаз' },
    { key: 'posting_number', label: 'Номер отправления' },
    { key: 'userstat', label: 'С' },
    { key: 'questions', label: 'В' },
    { key: 'cluster_to', label: 'Регион доставки' },
    { key: 'shipping_date', label: 'Дата отгрузки' },
    { key: 'fin_price', label: 'Заказ' },
    { key: 'price', label: 'Цена' },
    { key: 'profit', label: 'Прибыль' },
    { key: 'margin', label: 'Маржа' },
    { key: 'table_profit', label: 'Норма' },
    { key: 'table_margin', label: 'Табл. прибыль %' },
    { key: 'fin_customer_price', label: 'Покупатель' },
    { key: 'products', label: 'Товары' },
    { key: 'color', label: 'Цвет' },
    { key: 'returns', label: 'Возвраты' },
    { key: 'actions', label: 'Действия' },
    { key: 'processing_status', label: 'Статус обработки' },
];

const DEFAULT_TABLE_SETTINGS = {
    fontSize: 14,
    hiddenColumns: [],
    columnWidths: {},
    highlightPostingDigits: true,
    pinToolbar: false,
    scannerPortKey: '',
};

const DEFAULT_COLUMN_FILTERS = {
    posting_number: '',
    shipping_date: '',
    products: '',
};

const HIST_PRESETS = [
    { days: 3, label: '3 дня' },
    { days: 7, label: '7 дней' },
    { days: 14, label: '14 дней' },
    { days: 30, label: '30 дней' },
];

const OZON_UI_FILTERS_KEY = 'ozonOrdersUiFilters';

const formatHistLabel = (days) => {
    const n = Number(days);
    if (!Number.isFinite(n) || n < 1) return '3 дня';
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return `${n} день`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} дня`;
    return `${n} дней`;
};

const formatFinancialPrice = (value) => {
    const price = Number(value);
    if (!Number.isFinite(price)) return '—';
    return `${price.toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })} ₽`;
};

const formatProfitMoney = (value) =>
    `${Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽`;

const formatProfitPercent = (value) => `${Number(value).toFixed(1).replace('.', ',')}%`;

const profitTone = (value) => (value < 0 ? '#ff4d4f' : '#237804');

const getProductListPrice = (product, record) => {
    const detail = (record.financial_products || []).find(
        (item) => String(item.product_id) === String(product.sku)
    );
    const customerPrice = parseFloat(detail?.customer_price);
    const amount = Number.isFinite(customerPrice) ? customerPrice : parseFloat(product.price);
    return Number.isFinite(amount) ? amount : 0;
};

const tariffChargeValue = (value) => {
    if (value && typeof value === 'object') {
        return value.amount ?? value.price ?? value.value ?? 0;
    }
    return value || 0;
};

const getSerialApi = () =>
    typeof navigator !== 'undefined' && navigator.serial ? navigator.serial : null;

const playScannerSound = (source) => {
    try {
        const audio = new Audio(source);
        audio.play().catch(() => {
            // Браузер может заблокировать звук вне пользовательского действия.
        });
    } catch {
        // Звук не должен влиять на подключение сканера.
    }
};

const getSerialPortKey = (port, index = 0) => {
    const info = port?.getInfo?.() || {};
    const vendorId = Number.isFinite(info.usbVendorId) ? info.usbVendorId : 'unknown';
    const productId = Number.isFinite(info.usbProductId) ? info.usbProductId : 'unknown';
    return `${vendorId}:${productId}:${index}`;
};

const getSerialPortLabel = (port, index = 0) => {
    const info = port?.getInfo?.() || {};
    if (Number.isFinite(info.usbVendorId) || Number.isFinite(info.usbProductId)) {
        const vendor = Number.isFinite(info.usbVendorId)
            ? `0x${info.usbVendorId.toString(16).padStart(4, '0')}`
            : '—';
        const product = Number.isFinite(info.usbProductId)
            ? `0x${info.usbProductId.toString(16).padStart(4, '0')}`
            : '—';
        return `Порт ${index + 1} (${vendor}:${product})`;
    }
    return `Порт ${index + 1}`;
};

const loadUiFilters = () => {
    try {
        const raw = localStorage.getItem(OZON_UI_FILTERS_KEY);
        if (!raw) {
            return {
                columnFilters: { ...DEFAULT_COLUMN_FILTERS },
                selectedDisplayTypes: [],
                sortByDisplayType: false,
            };
        }
        const parsed = JSON.parse(raw);
        const columnFilters = parsed.columnFilters && typeof parsed.columnFilters === 'object'
            ? {
                  posting_number: String(parsed.columnFilters.posting_number || ''),
                  shipping_date: String(parsed.columnFilters.shipping_date || ''),
                  products: String(parsed.columnFilters.products || ''),
              }
            : { ...DEFAULT_COLUMN_FILTERS };
        return {
            columnFilters,
            selectedDisplayTypes: Array.isArray(parsed.selectedDisplayTypes)
                ? parsed.selectedDisplayTypes
                      .map((item) => String(item).trim())
                      .filter(Boolean)
                : [],
            sortByDisplayType: parsed.sortByDisplayType === true,
        };
    } catch {
        return {
            columnFilters: { ...DEFAULT_COLUMN_FILTERS },
            selectedDisplayTypes: [],
            sortByDisplayType: false,
        };
    }
};

const loadTableSettings = () => {
    try {
        const raw = localStorage.getItem(OZON_TABLE_SETTINGS_KEY);
        if (!raw) return { ...DEFAULT_TABLE_SETTINGS, columnWidths: {} };
        const parsed = JSON.parse(raw);
        const fontSize = Number(parsed.fontSize);
        const columnWidths =
            parsed.columnWidths && typeof parsed.columnWidths === 'object'
                ? Object.fromEntries(
                      Object.entries(parsed.columnWidths).filter(
                          ([, value]) => typeof value === 'number' && value >= 40
                      )
                  )
                : {};
        return {
            fontSize:
                Number.isFinite(fontSize) && fontSize >= 10 && fontSize <= 22
                    ? fontSize
                    : DEFAULT_TABLE_SETTINGS.fontSize,
            hiddenColumns: Array.isArray(parsed.hiddenColumns)
                ? parsed.hiddenColumns.filter((key) =>
                      TABLE_COLUMN_OPTIONS.some((option) => option.key === key)
                  )
                : [],
            columnWidths,
            highlightPostingDigits: parsed.highlightPostingDigits !== false,
            pinToolbar: parsed.pinToolbar === true,
            scannerPortKey:
                typeof parsed.scannerPortKey === 'string' ? parsed.scannerPortKey : '',
        };
    } catch (error) {
        return { ...DEFAULT_TABLE_SETTINGS, columnWidths: {} };
    }
};

const ResizableTitle = (props) => {
    const { onResize, onResizeStop, onResetWidth, width, ...restProps } = props;
    if (!width || !onResize) {
        return <th {...restProps} />;
    }
    return (
        <th
            {...restProps}
            style={{ ...restProps.style, width, minWidth: width, maxWidth: width, position: 'relative' }}
        >
            {restProps.children}
            <span
                className="ozon-col-resize-handle"
                title="Потяните, чтобы изменить ширину. Двойной клик — сбросить"
                onClick={(event) => event.stopPropagation()}
                onDoubleClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onResetWidth?.();
                }}
                onPointerDown={(event) => {
                    if (event.button !== 0) return;
                    event.preventDefault();
                    event.stopPropagation();
                    const startX = event.clientX;
                    const startWidth = width;
                    let lastWidth = startWidth;
                    let frame = 0;
                    const applyWidth = (nextWidth) => {
                        lastWidth = Math.max(48, nextWidth);
                        if (frame) cancelAnimationFrame(frame);
                        frame = requestAnimationFrame(() => onResize(lastWidth));
                    };
                    const onPointerMove = (moveEvent) => {
                        applyWidth(startWidth + moveEvent.clientX - startX);
                    };
                    const onPointerUp = () => {
                        if (frame) cancelAnimationFrame(frame);
                        document.removeEventListener('pointermove', onPointerMove);
                        document.removeEventListener('pointerup', onPointerUp);
                        document.body.classList.remove('ozon-col-resizing');
                        onResizeStop?.(lastWidth);
                    };
                    document.body.classList.add('ozon-col-resizing');
                    document.addEventListener('pointermove', onPointerMove);
                    document.addEventListener('pointerup', onPointerUp);
                }}
            />
        </th>
    );
};

const statusLabels = {
    awaiting_packaging: 'Ожидает упаковки',
    awaiting_deliver: 'Ожидает отправки',
    delivering: 'Доставляется',
    delivered: 'Доставлен',
    cancelled: 'Отменён'
};

const OzoneOrders = observer(() => {
    const { orders, loading, filters, statistics, orderStatusCounts, orderStatuses, returnsInfo, quickFilter, quickFilterCounts, isLabelPrintLocked, labelPrintCountdown, labelTimerRemaining } = ozonOrdersStore;
    const [collectingOrders, setCollectingOrders] = useState(new Set());
    const [togglingStatus, setTogglingStatus] = useState(new Set());
    const [issuingReturns, setIssuingReturns] = useState(new Set());
    const [v4Test, setV4Test] = useState({ open: false, loading: false, data: null, error: '' });
    const [copiedPostingNumber, setCopiedPostingNumber] = useState(null);
    const copiedPostingTimeoutRef = useRef(null);
    const [showSettingsCard, setShowSettingsCard] = useState(false);
    const [histMenuOpen, setHistMenuOpen] = useState(false);
    const [typeMenuOpen, setTypeMenuOpen] = useState(false);
    const [tableSettings, setTableSettings] = useState(loadTableSettings);
    const [isLoadingSelected, setIsLoadingSelected] = useState(false);
    const [priceDetailsOrder, setPriceDetailsOrder] = useState(null);
    const [isLoadingUnprinted, setIsLoadingUnprinted] = useState(false);
    const [stockPreviewOpen, setStockPreviewOpen] = useState(false);
    const [stockPreviewLoading, setStockPreviewLoading] = useState(false);
    const [stockPreview, setStockPreview] = useState(null);
    const [columnFilters, setColumnFilters] = useState(
        () => loadUiFilters().columnFilters
    );
    const [editingStock, setEditingStock] = useState(null);
    const [savingStock, setSavingStock] = useState(false);
    const [costItems, setCostItems] = useState([]);
    const [costCatalog, setCostCatalog] = useState([]);
    const [productDisplayRules, setProductDisplayRules] = useState([]);
    const [nameReplacements, setNameReplacements] = useState([]);
    const [selectedDisplayTypes, setSelectedDisplayTypes] = useState(
        () => loadUiFilters().selectedDisplayTypes
    );
    const [sortByDisplayType, setSortByDisplayType] = useState(
        () => loadUiFilters().sortByDisplayType
    );
    const [tableScrollY, setTableScrollY] = useState(360);
    const tableWrapRef = useRef(null);
    const scannerPortRef = useRef(null);
    const scannerReaderRef = useRef(null);
    const scannerReadingRef = useRef(false);
    const scannerMountedRef = useRef(true);
    const [scannerPorts, setScannerPorts] = useState([]);
    const [scannerStatus, setScannerStatus] = useState(() =>
        getSerialApi() ? 'disconnected' : 'unsupported'
    );
    const [scannerError, setScannerError] = useState('');
    const navigate = useNavigate();

    const refreshScannerPorts = useCallback(async () => {
        const serial = getSerialApi();
        if (!serial) {
            setScannerStatus('unsupported');
            setScannerPorts([]);
            return [];
        }

        try {
            const ports = await serial.getPorts();
            if (scannerMountedRef.current) setScannerPorts(ports);
            return ports;
        } catch (error) {
            if (scannerMountedRef.current) {
                setScannerError('Не удалось получить список разрешённых портов');
            }
            return [];
        }
    }, []);

    const disconnectScanner = useCallback(async ({ silent = false } = {}) => {
        scannerReadingRef.current = false;
        const reader = scannerReaderRef.current;
        scannerReaderRef.current = null;

        try {
            await reader?.cancel();
        } catch {
            // Поток уже может быть закрыт устройством.
        }
        try {
            reader?.releaseLock();
        } catch {
            // Lock уже освобождён.
        }

        const port = scannerPortRef.current;
        scannerPortRef.current = null;
        try {
            if (port) await port.close();
        } catch {
            // Порт может быть отключён на уровне ОС.
        }

        if (scannerMountedRef.current) setScannerStatus(getSerialApi() ? 'disconnected' : 'unsupported');
        if (!silent) {
            playScannerSound(disconnectSound);
            message.info({ content: 'Сканер отключён', duration: 2 });
        }
    }, []);

    const readScannerMessages = useCallback(async (port) => {
        const decoder = new TextDecoder();
        let pending = '';

        try {
            while (scannerReadingRef.current && port.readable) {
                const reader = port.readable.getReader();
                scannerReaderRef.current = reader;
                try {
                    while (scannerReadingRef.current) {
                        const { value, done } = await reader.read();
                        if (done) break;
                        pending += decoder.decode(value, { stream: true });
                        const messages = pending.split(/\r\n|\n|\r/);
                        pending = messages.pop() || '';
                        messages
                            .map((item) => item.trim())
                            .filter(Boolean)
                            .forEach((barcode) => {
                                message.info({
                                    content: `Сканер: ${barcode}`,
                                    duration: 2,
                                });
                            });
                    }
                } finally {
                    reader.releaseLock();
                    if (scannerReaderRef.current === reader) {
                        scannerReaderRef.current = null;
                    }
                }
            }
        } catch (error) {
            if (scannerReadingRef.current && scannerMountedRef.current) {
                setScannerError('Соединение со сканером прервано');
                setScannerStatus('disconnected');
                message.warning({
                    content: 'Соединение со сканером прервано',
                    duration: 2,
                });
            }
        } finally {
            if (scannerReadingRef.current && scannerMountedRef.current) {
                scannerPortRef.current = null;
                setScannerStatus('disconnected');
            }
            scannerReadingRef.current = false;
        }
    }, []);

    const connectScanner = useCallback(async () => {
        const serial = getSerialApi();
        if (!serial) {
            message.warning({
                content: 'Подключение COM-портов доступно в Chrome или Edge по HTTPS',
                duration: 3,
            });
            return;
        }

        setScannerError('');
        setScannerStatus('connecting');
        try {
            let ports = await refreshScannerPorts();
            let port = ports.find(
                (item, index) => getSerialPortKey(item, index) === tableSettings.scannerPortKey
            );

            if (!port) {
                port = await serial.requestPort();
                ports = await refreshScannerPorts();
                const selectedIndex = ports.indexOf(port);
                updateTableSettings({
                    scannerPortKey: getSerialPortKey(port, Math.max(selectedIndex, 0)),
                });
            }

            await port.open({ baudRate: 9600 });
            scannerPortRef.current = port;
            scannerReadingRef.current = true;
            setScannerStatus('connected');
            playScannerSound(connectSound);
            message.success({ content: 'Сканер подключён', duration: 2 });
            void readScannerMessages(port);
        } catch (error) {
            const cancelled = error?.name === 'NotFoundError';
            if (scannerMountedRef.current) {
                setScannerStatus('disconnected');
                setScannerError(
                    cancelled
                        ? 'Выбор порта отменён'
                        : error?.message || 'Не удалось подключить сканер'
                );
            }
        }
    }, [readScannerMessages, refreshScannerPorts, tableSettings.scannerPortKey]);

    useEffect(() => {
        scannerMountedRef.current = true;
        void refreshScannerPorts();
        return () => {
            scannerMountedRef.current = false;
            void disconnectScanner({ silent: true });
        };
    }, [disconnectScanner, refreshScannerPorts]);

    useEffect(() => {
        const fetchProductDisplayStyles = async () => {
            try {
                const token = localStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };
                const { data } = await axios.get(
                    `${API_URL}/v1/Settings/getProductDisplayStyles`,
                    { headers }
                );
                setProductDisplayRules(Array.isArray(data?.rules) ? data.rules : []);
            } catch (error) {
                console.error('Error fetching product display styles:', error);
                setProductDisplayRules([]);
            }
        };
        const fetchNameReplacements = async () => {
            try {
                const token = localStorage.getItem('token');
                const { data } = await axios.get(
                    `${API_URL}/v1/Settings/getProductNameReplacements`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setNameReplacements(Array.isArray(data?.items) ? data.items : []);
            } catch (error) {
                console.error('Error fetching product name replacements:', error);
                setNameReplacements([]);
            }
        };
        fetchProductDisplayStyles();
        fetchNameReplacements();
    }, []);

    const resolveProductDisplayStyle = (offerId) => {
        const needle = String(offerId || '').toLowerCase();
        if (!needle || !productDisplayRules.length) return null;
        const matched = productDisplayRules.find((rule) => {
            const article = String(rule.article || '').trim().toLowerCase();
            return Boolean(article) && needle.includes(article);
        });
        if (!matched) return null;
        const fontStyle = matched.fontStyle || 'normal';
        return {
            backgroundColor: matched.backgroundColor || undefined,
            color: matched.textColor || undefined,
            fontWeight:
                fontStyle === 'bold' || fontStyle === 'bold-italic' ? 700 : 400,
            fontStyle:
                fontStyle === 'italic' || fontStyle === 'bold-italic'
                    ? 'italic'
                    : 'normal',
            borderColor: matched.backgroundColor || undefined,
        };
    };

    const getProductOfferId = (product) =>
        String(product?.offer_id || product?.color?._id || '');

    const getOrderDisplayTypeIndex = (order) => {
        let best = productDisplayRules.length;
        (order.products || []).forEach((product) => {
            const offerId = getProductOfferId(product).toLowerCase();
            if (!offerId) return;
            const index = productDisplayRules.findIndex((rule) => {
                const article = String(rule.article || '').trim().toLowerCase();
                return Boolean(article) && offerId.includes(article);
            });
            if (index !== -1 && index < best) {
                best = index;
            }
        });
        return best;
    };

    const orderMatchesDisplayTypes = (order, typeArticles) => {
        if (!typeArticles.length) return true;
        return (order.products || []).some((product) => {
            const offerId = getProductOfferId(product).toLowerCase();
            return typeArticles.some((article) =>
                offerId.includes(String(article).toLowerCase())
            );
        });
    };

    const toggleDisplayTypeFilter = (article) => {
        const key = String(article || '').trim();
        if (!key) return;
        setSelectedDisplayTypes((prev) =>
            prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
        );
    };

    const buildStockUpdateItems = (article, present) => {
        const items = [{ article, present }];
        if (String(article).startsWith('HB')) {
            const numericPart = String(article).match(/\d+/)?.[0];
            if (numericPart) {
                [
                    `HB-PRO-${numericPart}`,
                    `HB-EXT-${numericPart}`,
                    `HB-STD-${numericPart}`,
                ].forEach((variant) => {
                    if (variant !== article) {
                        items.push({ article: variant, present });
                    }
                });
            }
        }
        return items;
    };

    const saveStockQuantity = async () => {
        if (!editingStock?.offerId) return;
        const present = Number(editingStock.value);
        if (!Number.isFinite(present) || present < 0) {
            message.warning('Укажите корректное количество');
            return;
        }
        const items = buildStockUpdateItems(editingStock.offerId, present);
        setSavingStock(true);
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.put(
                `${API_URL}/v1/stocks/update-quantities`,
                { items },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const presentByArticle = {};
            items.forEach((item) => {
                presentByArticle[item.article] = item.present;
            });
            (data?.results || []).forEach((result) => {
                if (result?.offer_id && result.updated && !(result.errors || []).length) {
                    presentByArticle[result.offer_id] = present;
                }
            });
            ozonOrdersStore.updateProductPresent(presentByArticle);

            const failed = (data?.results || []).filter(
                (result) => !result.updated || (result.errors || []).length
            );
            if (failed.length && data?.status !== 'success') {
                message.warning(data?.message || 'Остатки обновлены частично');
            } else {
                message.success('Остаток обновлён');
            }
            setEditingStock(null);
        } catch (error) {
            const detail =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                'Не удалось обновить остаток';
            message.error(detail);
        } finally {
            setSavingStock(false);
        }
    };

    const openStockPreview = async (offerId) => {
        if (!offerId) {
            message.warning('Нет артикула товара');
            return;
        }
        setStockPreviewOpen(true);
        setStockPreview(null);
        setStockPreviewLoading(true);
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(
                `${API_URL}/v1/stocks/item/${encodeURIComponent(offerId)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setStockPreview(data);
        } catch (error) {
            const detail =
                error.response?.data?.detail ||
                error.message ||
                'Не удалось загрузить товар';
            message.error(detail);
            setStockPreviewOpen(false);
        } finally {
            setStockPreviewLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (copiedPostingTimeoutRef.current) {
                clearTimeout(copiedPostingTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        Promise.all([
            axios.get(`${API_URL}/v1/cost/items`, { headers }),
            axios.get(`${API_URL}/v1/cost/catalog`, { headers }),
        ])
            .then(([itemsResponse, catalogResponse]) => {
                setCostItems(itemsResponse.data || []);
                setCostCatalog(catalogResponse.data || []);
            })
            .catch((error) => {
                console.error('Error fetching cost data:', error);
            });
    }, []);

    const copyPostingNumber = async (postingNumber) => {
        const cleanNumber = String(postingNumber || '').replace(/\s+/g, '');
        if (!cleanNumber) return;

        const copyWithFallback = () => {
            const textarea = document.createElement('textarea');
            textarea.value = cleanNumber;
            textarea.setAttribute('readonly', '');
            textarea.style.cssText =
                'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:none;opacity:0;';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            textarea.setSelectionRange(0, cleanNumber.length);
            const ok = document.execCommand('copy');
            document.body.removeChild(textarea);
            if (!ok) throw new Error('execCommand copy failed');
        };

        try {
            if (navigator.clipboard?.writeText && window.isSecureContext) {
                try {
                    await navigator.clipboard.writeText(cleanNumber);
                } catch (_) {
                    copyWithFallback();
                }
            } else {
                copyWithFallback();
            }
            if (copiedPostingTimeoutRef.current) {
                clearTimeout(copiedPostingTimeoutRef.current);
            }
            setCopiedPostingNumber(cleanNumber);
            copiedPostingTimeoutRef.current = setTimeout(() => {
                setCopiedPostingNumber(null);
                copiedPostingTimeoutRef.current = null;
            }, 2000);
        } catch (error) {
            message.error('Не удалось скопировать номер отправления');
        }
    };

    const updateTableSettings = (patch) => {
        setTableSettings((current) => {
            const next = { ...current, ...patch };
            localStorage.setItem(OZON_TABLE_SETTINGS_KEY, JSON.stringify(next));
            return next;
        });
    };

    const updateColumnWidth = (key, width, persist = true) => {
        setTableSettings((current) => {
            const next = {
                ...current,
                columnWidths: {
                    ...(current.columnWidths || {}),
                    [key]: width,
                },
            };
            if (persist) {
                localStorage.setItem(OZON_TABLE_SETTINGS_KEY, JSON.stringify(next));
            }
            return next;
        });
    };

    const resetColumnWidth = (key) => {
        setTableSettings((current) => {
            const columnWidths = { ...(current.columnWidths || {}) };
            delete columnWidths[key];
            const next = { ...current, columnWidths };
            localStorage.setItem(OZON_TABLE_SETTINGS_KEY, JSON.stringify(next));
            return next;
        });
    };

    const resetTableSettings = () => {
        const next = { ...DEFAULT_TABLE_SETTINGS, columnWidths: {} };
        setTableSettings(next);
        localStorage.setItem(OZON_TABLE_SETTINGS_KEY, JSON.stringify(next));
        setColumnFilters({ ...DEFAULT_COLUMN_FILTERS });
    };

    const getColumnWidth = (key, fallback) => {
        const saved = tableSettings.columnWidths?.[key];
        return typeof saved === 'number' && saved >= 40 ? saved : fallback;
    };

    const createTextColumnFilter = (filterKey) => ({
        filterDropdown: () => (
            <div style={{ padding: 8 }} onKeyDown={(event) => event.stopPropagation()}>
                <Input
                    allowClear
                    autoFocus
                    placeholder="Часть слова..."
                    value={columnFilters[filterKey]}
                    onChange={(event) => {
                        const value = event.target.value;
                        setColumnFilters((prev) => ({ ...prev, [filterKey]: value }));
                    }}
                    style={{ width: 220 }}
                />
            </div>
        ),
        filterIcon: () => (
            <SearchOutlined
                style={{
                    color: columnFilters[filterKey]?.trim() ? '#1677ff' : undefined,
                }}
            />
        ),
        filteredValue: columnFilters[filterKey]?.trim()
            ? [columnFilters[filterKey]]
            : null,
    });

    const getShippingSearchText = (record) => {
        if (!record?.tariffication?.next_tariff_starts_at) return '';
        const shippingDate = moment(record.tariffication.next_tariff_starts_at);
        return [
            shippingDate.format('D.MM'),
            shippingDate.format('HH:mm'),
            tariffChargeValue(record.tariffication.current_tariff_charge),
            tariffChargeValue(record.tariffication.next_tariff_charge),
            record.tariffication.current_tariff_type,
            record.tariffication.next_tariff_type,
        ]
            .filter((value) => value !== undefined && value !== null && value !== '')
            .join(' ')
            .toLowerCase();
    };

    const getProductsSearchText = (record) =>
        (record.products || [])
            .map((product) =>
                [
                    product.offer_id,
                    product.shortName,
                    product.name,
                    product.present,
                    product.price,
                    product?.color?._id,
                    product?.color?.shortName,
                    product?.color?.color,
                    applyNameReplacements(product?.color?.shortName || product.shortName || product.name, nameReplacements),
                ]
                    .filter((value) => value !== undefined && value !== null && value !== '')
                    .join(' ')
            )
            .join(' ')
            .toLowerCase();

    const postingFilter = columnFilters.posting_number.trim().toLowerCase();
    const shippingFilter = columnFilters.shipping_date.trim().toLowerCase();
    const productsFilter = columnFilters.products.trim().toLowerCase();
    const filteredOrders = (() => {
        let result = orders.filter((order) => {
            if (
                postingFilter &&
                !String(order.posting_number || '')
                    .replace(/\s+/g, '')
                    .toLowerCase()
                    .includes(postingFilter)
            ) {
                return false;
            }
            if (shippingFilter && !getShippingSearchText(order).includes(shippingFilter)) {
                return false;
            }
            if (productsFilter && !getProductsSearchText(order).includes(productsFilter)) {
                return false;
            }
            if (!orderMatchesDisplayTypes(order, selectedDisplayTypes)) {
                return false;
            }
            return true;
        });
        if (sortByDisplayType && productDisplayRules.length) {
            result = [...result].sort(
                (a, b) => getOrderDisplayTypeIndex(a) - getOrderDisplayTypeIndex(b)
            );
        }
        return result;
    })();

    const screenStats = (() => {
        let baseSum = 0;
        let customerSum = 0;
        let profitSum = 0;
        let marginSum = 0;
        let marginCount = 0;
        filteredOrders.forEach((order) => {
            const fin = Number(order.Fin_price);
            if (Number.isFinite(fin)) baseSum += fin;
            (order.products || []).forEach((product) => {
                const qty = Number(product.quantity) > 0 ? Number(product.quantity) : 1;
                customerSum += getProductListPrice(product, order) * qty;
                const econ = productEconomics(product, costItems, costCatalog);
                if (!econ) return;
                profitSum += econ.orderProfit;
                marginSum += econ.orderMargin;
                marginCount += 1;
            });
        });
        return {
            count: filteredOrders.length,
            baseSum,
            customerSum,
            profitSum,
            avgMargin: marginCount ? marginSum / marginCount : null,
            realMargin: customerSum > 0 ? (profitSum / customerSum) * 100 : null,
        };
    })();

    const availableDisplayRules = productDisplayRules.filter((rule) => {
        const article = String(rule.article || '').trim().toLowerCase();
        if (!article) return false;
        return orders.some((order) =>
            (order.products || []).some((product) =>
                getProductOfferId(product).toLowerCase().includes(article)
            )
        );
    });

    useEffect(() => {
        if (!selectedDisplayTypes.length) return;
        if (!productDisplayRules.length) return;
        const available = new Set(
            availableDisplayRules.map((rule) => String(rule.article || '').trim())
        );
        setSelectedDisplayTypes((prev) => {
            const next = prev.filter((article) => available.has(article));
            return next.length === prev.length ? prev : next;
        });
    }, [orders, productDisplayRules]);

    useEffect(() => {
        try {
            localStorage.setItem(
                OZON_UI_FILTERS_KEY,
                JSON.stringify({
                    columnFilters,
                    selectedDisplayTypes,
                    sortByDisplayType,
                })
            );
        } catch {
            /* ignore */
        }
    }, [columnFilters, selectedDisplayTypes, sortByDisplayType]);

    const fetchData = useCallback(async () => {
        await ozonOrdersStore.fetchOrders();
        if (ozonOrdersStore.error) {
            return;
        }
        message.success({ content: 'Данные обновлены', key: 'loading' });
    }, []);

    useEffect(() => {
        if (ozonOrdersStore.rawOrders.length > 0 || ozonOrdersStore.loading) {
            return;
        }
        message.loading({ content: 'Загрузка данных...', key: 'loading' });
        void (async () => {
            const loaded = await ozonOrdersStore.ensureOrdersLoaded();
            if (!loaded || ozonOrdersStore.error) {
                return;
            }
            message.success({ content: 'Данные обновлены', key: 'loading' });
        })();
    }, []);

    useEffect(() => {
        ozonOrdersStore.startStatusPolling();
        return () => ozonOrdersStore.stopStatusPolling();
    }, []);

    useEffect(() => {
        const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile) {
            navigate('/orders/ozonm', { replace: true });
        }
    }, []);

    const handleCollect = async (postingNumber) => {
        setCollectingOrders(prev => new Set([...prev, postingNumber]));
        try {
            const result = await ozonOrdersStore.collectOrder(postingNumber);
            message.success(
                ozonOrdersStore.isLabelPrintLockedFor(postingNumber)
                    ? `${result.message}. Печать через ${ozonOrdersStore.labelPrintCountdownFor(postingNumber)}`
                    : result.message
            );
        } catch (error) {
            message.error(error.response?.data?.message || error.message || 'Ошибка при сборке заказа');
        } finally {
            setCollectingOrders(prev => {
                const newSet = new Set(prev);
                newSet.delete(postingNumber);
                return newSet;
            });
        }
    };

    const showLabelResult = (result, key) => {
        if (result?.warning) {
            message.warning({ content: result.message, key, duration: 8 });
        } else {
            message.success({ content: result?.message || 'Этикетка успешно загружена', key });
        }
    };

    const handlePrint = async (postingNumber) => {
        if (ozonOrdersStore.isLabelPrintLockedFor(postingNumber)) {
            message.warning(`Печать будет доступна через ${ozonOrdersStore.labelPrintCountdownFor(postingNumber)}`);
            return;
        }
        try {
            message.loading({ content: 'Загрузка этикетки...', key: 'printing' });
            showLabelResult(await ozonOrdersStore.printPostingLabel(postingNumber), 'printing');
        } catch (error) {
            message.error({ 
                content: error.response?.data?.message || error.message || 'Ошибка при загрузке этикетки', 
                key: 'printing' 
            });
        }
    };

    const handleRePrint = async (postingNumber) => {
        if (ozonOrdersStore.isLabelPrintLockedFor(postingNumber)) {
            message.warning(`Печать будет доступна через ${ozonOrdersStore.labelPrintCountdownFor(postingNumber)}`);
            return;
        }
        try {
            message.loading({ content: 'Загрузка этикетки...', key: 'printing' });
            showLabelResult(await ozonOrdersStore.RePrintPostingLabel(postingNumber), 'printing');
        } catch (error) {
            message.error({
                content: error.response?.data?.message || error.message || 'Ошибка при загрузке этикетки',
                key: 'printing'
            });
        }
    };

    const getOrderStatus = (postingNumber) => {
        const cleanPostingNumber = postingNumber.replace(/\n/g, '');
        return {
            isPrinted: orderStatuses.printedOrders.includes(cleanPostingNumber),
            isCollected: orderStatuses.collectOrders.includes(cleanPostingNumber)
        };
    };

    const getStatusIcons = (postingNumber, statuses) => {
        const printKey = `print:${postingNumber}`;
        const collectKey = `collect:${postingNumber}`;
        return (
            <Space>
                <Button
                    size="small"
                    type={statuses.isPrinted ? 'primary' : 'default'}
                    icon={<PrinterOutlined />}
                    loading={togglingStatus.has(printKey)}
                    title={statuses.isPrinted ? 'Распечатан — снять отметку' : 'Отметить распечатанным'}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleTogglePrinted(postingNumber);
                    }}
                />
                <Button
                    size="small"
                    type={statuses.isCollected ? 'primary' : 'default'}
                    icon={<CodeSandboxOutlined />}
                    loading={togglingStatus.has(collectKey)}
                    title={statuses.isCollected ? 'Собран — снять отметку' : 'Отметить собранным'}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleToggleCollected(postingNumber);
                    }}
                />
            </Space>
        );
    };

    const handleTogglePrinted = async (postingNumber) => {
        const key = `print:${postingNumber}`;
        if (togglingStatus.has(key)) return;
        setTogglingStatus((prev) => new Set(prev).add(key));
        try {
            const printed = await ozonOrdersStore.togglePrinted(postingNumber);
            message.success(printed ? 'Отмечено как распечатанное' : 'Отметка о печати снята');
        } catch (error) {
            message.error(error.response?.data?.message || error.message || 'Не удалось изменить статус печати');
        } finally {
            setTogglingStatus((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    const handleToggleCollected = async (postingNumber) => {
        const key = `collect:${postingNumber}`;
        if (togglingStatus.has(key)) return;
        setTogglingStatus((prev) => new Set(prev).add(key));
        try {
            const collected = await ozonOrdersStore.toggleCollected(postingNumber);
            message.success(collected ? 'Отмечено как собранное' : 'Отметка о сборке снята');
        } catch (error) {
            message.error(error.response?.data?.message || error.message || 'Не удалось изменить статус сборки');
        } finally {
            setTogglingStatus((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    const handleV4Test = async () => {
        setV4Test({ open: true, loading: true, data: null, error: '' });
        try {
            const response = await axios.get(`${API_URL}/v1/orders/ozon/v4/list`, {
                params: { hist: filters.hist || 3 },
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            setV4Test({ open: true, loading: false, data: response.data, error: '' });
        } catch (error) {
            setV4Test({
                open: true,
                loading: false,
                data: null,
                error: error.response?.data?.message || error.message || 'Ошибка запроса v4',
            });
        }
    };

    const handleIssueReturn = async (postingNumber, article) => {
        const key = `${postingNumber}::${article}`;
        if (issuingReturns.has(key)) return;
        setIssuingReturns((prev) => new Set(prev).add(key));
        try {
            const result = await ozonOrdersStore.issueReturn(postingNumber, article);
            message.success(`${article}: взят из возвратов, осталось ${result.quantity}`);
        } catch (error) {
            message.error(
                error.response?.data?.detail || error.message || 'Не удалось списать возврат'
            );
            void ozonOrdersStore.fetchReturns();
        } finally {
            setIssuingReturns((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    const renderReturnsCell = (products, record) => {
        const cleanPostingNumber = String(record.posting_number || '').replace(/\n/g, '');
        const { returns, issued } = returnsInfo;
        const issuedArticles = issued[cleanPostingNumber] || [];
        return (
            <Space direction="vertical" size={4}>
                {(products || []).map((product, index) => {
                    const article = product.offer_id || product?.color?._id;
                    const lineKey = `returns-${article || 'product'}-${index}`;
                    const lineStyle = { minHeight: 24, display: 'flex', alignItems: 'center', gap: 6 };
                    if (article && issuedArticles.includes(article)) {
                        return (
                            <div key={lineKey} style={lineStyle} title="Отправлен из возвратов">
                                <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                            </div>
                        );
                    }
                    const quantity = article ? Number(returns[article]) || 0 : 0;
                    if (quantity <= 0) {
                        return <div key={lineKey} style={lineStyle} />;
                    }
                    const issuing = issuingReturns.has(`${cleanPostingNumber}::${article}`);
                    return (
                        <div key={lineKey} style={lineStyle}>
                            <ExclamationCircleFilled
                                style={{ color: '#fa8c16', fontSize: 16 }}
                                title={`${article} есть в возвратах`}
                            />
                            <span style={{ fontWeight: 600 }}>{quantity}</span>
                            <Button
                                size="small"
                                icon={<ExportOutlined />}
                                loading={issuing}
                                title="Взять из возвратов для этого заказа"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleIssueReturn(cleanPostingNumber, article);
                                }}
                            />
                        </div>
                    );
                })}
            </Space>
        );
    };

    const getTagColor = (postingNumber) => {
        const cleanPostingNumber = postingNumber.replace(/\n/g, '');
        return orderStatuses.collectOrders.includes(cleanPostingNumber) ? "#52c41a" : undefined;
    };

    const handleDownloadSelected = async () => {
        const selectedCount = ozonOrdersStore.selectedOrders.size;
        if (ozonOrdersStore.isLabelPrintLocked) {
            message.warning(`Печать будет доступна через ${ozonOrdersStore.labelPrintCountdown}`);
            return;
        }
        if (selectedCount === 0) {
            message.warning('Выберите заказы для скачивания');
            return;
        }
        try {
            setIsLoadingSelected(true);
            message.loading({ content: 'Загрузка этикеток...', key: 'downloading-selected' });
            showLabelResult(await ozonOrdersStore.downloadSelectedLabels(), 'downloading-selected');
        } catch (error) {
            message.error({ 
                content: error.response?.data?.message || error.message || 'Ошибка при загрузке этикеток', 
                key: 'downloading-selected' 
            });
        } finally {
            setIsLoadingSelected(false);
        }
    };

    const getUnprintedOrdersCount = () => {
        return orders.filter(order => {
            const cleanPostingNumber = order.posting_number.replace(/\n/g, '');
            return !orderStatuses.printedOrders.includes(cleanPostingNumber) && 
                   !orderStatuses.collectOrders.includes(cleanPostingNumber);
        }).length;
    };

    const handleDownloadUnprinted = async () => {
        const unprintedCount = getUnprintedOrdersCount();
        if (ozonOrdersStore.isLabelPrintLocked) {
            message.warning(`Печать будет доступна через ${ozonOrdersStore.labelPrintCountdown}`);
            return;
        }
        if (unprintedCount === 0) {
            message.warning('Нет нераспечатанных заказов');
            return;
        }
        try {
            setIsLoadingUnprinted(true);
            message.loading({ content: 'Загрузка этикеток...', key: 'downloading-unprinted' });
            showLabelResult(await ozonOrdersStore.downloadUnprintedLabels(), 'downloading-unprinted');
        } catch (error) {
            message.error({ 
                content: error.response?.data?.message || error.message || 'Ошибка при загрузке этикеток', 
                key: 'downloading-unprinted' 
            });
        } finally {
            setIsLoadingUnprinted(false);
        }
    };

    const formatShippingInfo = (record) => {
        if (!record.tariffication || !record.tariffication.next_tariff_starts_at) return null;

        dayjs.locale('ru');
        const shippingDate = moment(record.tariffication.next_tariff_starts_at).add(0, 'hours');
        const formattedDate = shippingDate.format('D.MM').toLowerCase();
        const formattedTime = shippingDate.format('HH:mm');
        // alert(JSON.stringify(record.tariffication.current_tariff_charge))
        // alert(formattedTime)
        const charge = tariffChargeValue(record.tariffication.current_tariff_charge);
        const potomcharge = tariffChargeValue(record.tariffication.next_tariff_charge);
        // alert(JSON.stringify(record.tariffication.current_tariff_charge))
        const  potomtype = record.tariffication.next_tariff_type || '';
        const type = record.tariffication.current_tariff_type || '';

        let priceText = '';
        let potomText = '';

        // if (type.toLowerCase().includes('no_discount')) {
        //     priceText = `Без скидки`;
        // } else
        // if (type.toLowerCase().includes('discount')) {
        //     priceText = `Скидка ${charge}₽`;
        // } else if (type.toLowerCase().includes('commission')) {
        //     priceText = `Наценка ${charge}₽`;
        // } else {
        //     priceText = ` `;
        // }

        if (type.toLowerCase().includes('no_discount')) {
            priceText = `0₽`;
        } else
        if (type.toLowerCase().includes('discount')) {
            priceText = `${charge}₽`;
        } else if (type.toLowerCase().includes('commission')) {
            priceText = `${charge}₽`;
        } else {
            priceText = ` `;
        }

        // if (potomtype.toLowerCase().includes('no_discount')) {
        //     potomText = `Без скидки`;
        // } else
        // if (potomtype.toLowerCase().includes('discount')) {
        //     potomText = `Скидка ${potomcharge}₽`;
        // } else if (potomtype.toLowerCase().includes('commission')) {
        //     potomText = `${potomcharge}₽`;
        // } else {
        //     potomText = ` `;
        // }
        if (potomtype.toLowerCase().includes('no_discount')) {
            potomText = `0₽`;
        } else
        if (potomtype.toLowerCase().includes('discount')) {
            potomText = `${potomcharge}₽`;
        } else if (potomtype.toLowerCase().includes('commission')) {
            potomText = `${potomcharge}₽`;
        } else {
            potomText = ` `;
        }

        return (
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <div style={{fontWeight: 'bold'}}>
                    {formattedDate} до {formattedTime}
                </div>
                {/*<div style={{color: '#666'}}>*/}
                {/*    до {formattedTime}*/}
                {/*</div>*/}
                <div>
                    <a style={{color: type.toLowerCase().includes('discount') ? '#52c41a' : '#f5222d'}}>{priceText} </a>
                    <a> --> </a>
                    <a style={{color: type.toLowerCase().includes('discount') ? '#52c41a' : '#f5222d'}}>{potomText} </a>
                    {/*<a style={{color: type.toLowerCase().includes('discount') ? '#52c41a' : '#f5222d'}}> -{{potomText} </a>*/}


                </div>
                {/*<div style={{color: type.toLowerCase().includes('discount') ? '#52c41a' : '#f5222d'}}>*/}
                {/*    {priceText} ->{potomText}*/}
                {/*</div>*/}
                {/*<div style={{color: potomtype.toLowerCase().includes('discount') ? '#52c41a' : '#f5222d'}}>*/}
                {/*    {potomText}*/}
                {/*</div>*/}
            </div>
        );
    };

    const columns = [
        {
            title: <Checkbox
                onChange={(e) => {
                    if (e.target.checked) {
                        filteredOrders.forEach(order => {
                            if (!ozonOrdersStore.isOrderSelected(order.posting_number)) {
                                ozonOrdersStore.toggleOrderSelection(order.posting_number);
                            }
                        });
                    } else {
                        ozonOrdersStore.clearSelectedOrders();
                    }
                }}
                checked={filteredOrders.length > 0 && filteredOrders.every(order => 
                    ozonOrdersStore.isOrderSelected(order.posting_number)
                )}
                indeterminate={filteredOrders.some(order => 
                    ozonOrdersStore.isOrderSelected(order.posting_number)
                ) && !filteredOrders.every(order => 
                    ozonOrdersStore.isOrderSelected(order.posting_number)
                )}
            />,
            dataIndex: 'posting_number',
            key: 'selection',
            width: 50,
            render: (postingNumber) => (
                <Checkbox
                    checked={ozonOrdersStore.isOrderSelected(postingNumber)}
                    onChange={() => ozonOrdersStore.toggleOrderSelection(postingNumber)}
                />
            ),
        },
        {
            title: 'Дата обработки',
            dataIndex: 'in_process_at',
            key: 'in_process_at',
            width: 100,
            align: 'center',
            render: (date) => (
                <div style={{ textAlign: 'center' }}>
                    {date}
                </div>
            )
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status, record) => (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Tag color={statusColors[status]} style={{ width: '100%', textAlign: 'center' }}>
                        {statusLabels[status]}
                    </Tag>
                    {record.dayoff && (
                        <Tag color="orange" style={{ width: '100%', textAlign: 'center' }}>
                            {record.dayoff}
                        </Tag>
                    )}
                </Space>
            ),
        },
        {
            title: 'Магаз',
            dataIndex: 'platform',
            key: 'platform',
            width: 10,
            render: (status, record) => (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {record.dayoff && (
                        <Tag color={record.platform_color} style={{ width: '100%', textAlign: 'center' }}>
                            {record.platform}
                        </Tag>
                    )}
                </Space>
            ),
        },
        {
            title: 'Номер отправления',
            dataIndex: 'posting_number',
            key: 'posting_number',
            width: 140,
            ...createTextColumnFilter('posting_number'),
            render: (text) => {
                const cleanNumber = String(text || '').replace(/\s+/g, '');
                if (copiedPostingNumber === cleanNumber) {
                    return (
                        <div style={{ textAlign: 'center', color: '#52c41a', fontSize: 12, lineHeight: 1.3 }}>
                            Номер отправления скопирован
                        </div>
                    );
                }
                const parts = cleanNumber.split('-');
                const first = parts[0] || '';
                const firstPrefix = first.length > 4 ? first.slice(0, -4) : '';
                const firstHighlight = first.length > 4 ? first.slice(-4) : first;
                const rest = parts.length > 1 ? `-${parts.slice(1).join('-')}` : '';
                const highlightDigits = tableSettings.highlightPostingDigits !== false;
                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                        }}
                    >
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {highlightDigits ? (
                                <>
                                    {firstPrefix}
                                    <span
                                        style={{
                                            color: '#fff',
                                            background: '#000',
                                            padding: '0 2px',
                                            borderRadius: 2,
                                        }}
                                    >
                                        {firstHighlight}
                                    </span>
                                    {rest}
                                </>
                            ) : (
                                cleanNumber
                            )}
                        </span>
                        <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            title="Скопировать"
                            aria-label="Скопировать"
                            onClick={(event) => {
                                event.stopPropagation();
                                copyPostingNumber(cleanNumber);
                            }}
                            style={{ padding: 0, height: 22, width: 22, minWidth: 22 }}
                        />
                    </div>
                );
            }
        },
        {
            title: 'С',
            dataIndex: 'ustat',
            key: 'userstat',
            width: 30,
            render: (ustat) => (
                <div style={{ whiteSpace: 'pre-line', textAlign: 'center', color:"black" }}>
                    {ustat?.userstat?.zakazCount > 0 && (
                        <Vozvraty20203 dta={ustat.userstat} voz={false}/>
                    )}
                    {ustat?.userstat?.cancelCount > 0 && (
                        <Vozvraty20203 dta={ustat.userstat} voz={true}/>
                    )}
                </div>
            )
        },
        {
            title: 'В',
            dataIndex: 'ustat',
            key: 'questions',
            width: 50,
            render: (ustat) => {

                if (!ustat?.questext && !(ustat?.reviews_count > 0)) return null;

                return (
                    <>
                    <Space direction="vertical" size={4}   style={{ width: '100%', color: "black" }} >
                        {ustat?.questext && (<Que2025 dta={ustat}/>)}
                        {ustat?.reviews_count > 0 && <Rewiews2025 dta={ustat} />}
                    </Space>
                    </>
                );


            }
        },
        {
            title: 'Регион доставки',
            dataIndex: 'cluster_to',
            key: 'cluster_to',
            width: 50,
        },
        {
            title: 'Дата отгрузки',
            dataIndex: 'tariffication',
            key: 'shipping_date',
            width: 200,
            ...createTextColumnFilter('shipping_date'),
            render: (_, record) => formatShippingInfo(record)
        },
        {
            title: 'Заказ',
            dataIndex: 'Fin_price',
            key: 'fin_price',
            width: 90,
            align: 'right',
            render: (value) => (
                <Tag color="green" style={{ width: '100%', color: 'black' }}>
                    {formatFinancialPrice(value)}
                </Tag>
            ),
        },
        {
            title: 'Цена',
            dataIndex: 'products',
            key: 'price',
            width: 30,
            render: (products, record) => (
                <Space direction="vertical" size={4} >
                    {products.map((product, index) => {
                        const detail = (record.financial_products || []).find(
                            (item) => String(item.product_id) === String(product.sku)
                        );
                        const customerPrice = parseFloat(detail?.customer_price);
                        const amount = Number.isFinite(customerPrice) ? customerPrice : parseFloat(product.price);
                        const sellerDiscountPercent = parseFloat(detail?.total_discount_percent);
                        return (
                            <Space key={`${product.sku || product.offer_id}-${index}`} direction="horizontal" size={4} >
                                <Tag
                                    color="green"
                                    style={{ width: '100%', color: "black", cursor: 'pointer' }}
                                    title="Показать детали цены"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setPriceDetailsOrder(record);
                                    }}
                                >
                                    {`${amount.toFixed(0)} руб`}
                                </Tag>
                                {sellerDiscountPercent > 17 ? (
                                    <Tooltip title={`Скидка продавца ${sellerDiscountPercent.toFixed(2)}%`}>
                                        <ExclamationCircleFilled style={{ color: '#ff4d4f', fontSize: 12 }} />
                                    </Tooltip>
                                ) : null}
                            </Space>
                        );
                    })}
                </Space>
            ),
        },
        {
            title: 'Прибыль',
            dataIndex: 'products',
            key: 'profit',
            width: 90,
            align: 'right',
            render: (products) => (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {(products || []).map((product, index) => {
                        const econ = productEconomics(product, costItems, costCatalog);
                        return (
                            <div
                                key={`profit-${product.sku || product.offer_id}-${index}`}
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 600,
                                    color: econ ? profitTone(econ.orderProfit) : '#8c8c8c',
                                    textAlign: 'right',
                                }}
                            >
                                {econ ? formatProfitMoney(econ.orderProfit) : '—'}
                            </div>
                        );
                    })}
                </Space>
            ),
        },
        {
            title: 'Маржа',
            dataIndex: 'products',
            key: 'margin',
            width: 70,
            align: 'right',
            render: (products) => (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {(products || []).map((product, index) => {
                        const econ = productEconomics(product, costItems, costCatalog);
                        return (
                            <div
                                key={`margin-${product.sku || product.offer_id}-${index}`}
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 600,
                                    color: econ ? profitTone(econ.orderProfit) : '#8c8c8c',
                                    textAlign: 'right',
                                }}
                            >
                                {econ ? formatProfitPercent(econ.orderMargin) : '—'}
                            </div>
                        );
                    })}
                </Space>
            ),
        },
        {
            title: 'Норма',
            dataIndex: 'products',
            key: 'table_profit',
            width: 80,
            align: 'right',
            render: (products) => (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {(products || []).map((product, index) => {
                        const econ = productEconomics(product, costItems, costCatalog);
                        return (
                            <div
                                key={`table-profit-${product.sku || product.offer_id}-${index}`}
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 400,
                                    fontSize: '0.85em',
                                    color: '#8c8c8c',
                                    textAlign: 'right',
                                }}
                            >
                                {econ ? formatProfitMoney(econ.tableProfit) : '—'}
                            </div>
                        );
                    })}
                </Space>
            ),
        },
        {
            title: 'Табл. %',
            dataIndex: 'products',
            key: 'table_margin',
            width: 64,
            align: 'right',
            render: (products) => (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {(products || []).map((product, index) => {
                        const econ = productEconomics(product, costItems, costCatalog);
                        return (
                            <div
                                key={`table-margin-${product.sku || product.offer_id}-${index}`}
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 400,
                                    fontSize: '0.85em',
                                    color: '#8c8c8c',
                                    textAlign: 'right',
                                }}
                            >
                                {econ ? formatProfitPercent(econ.tableMargin) : '—'}
                            </div>
                        );
                    })}
                </Space>
            ),
        },
        {
            title: 'Покупатель',
            dataIndex: 'Fin_customer_price',
            key: 'fin_customer_price',
            width: 110,
            align: 'right',
            render: (value) => formatFinancialPrice(value),
        },


        // {
        //     title: 'Товары',
        //     dataIndex: 'products',
        //     key: 'products',
        //     render: (products) => (
        //         <ul className="list-none p-0">
        //             {products.map((product, index) => (
        //                 <li key={index} className="mb-2">
        //                     <div className="font-bold">{product.shortName || product.name}</div>
        //                     <div className="text-sm text-gray-500">
        //                         Артикул: {product.offer_id} | Цена: {product.price} ₽ | Кол-во: {product.quantity}
        //                     </div>
        //                 </li>
        //             ))}
        //         </ul>
        //     ),
        // },
        {
            title: 'Товары',
            dataIndex: 'products',
            key: 'products',
            width: 220,
            ...createTextColumnFilter('products'),
            render: (products, record) => {
                const tagColor = getTagColor(record.posting_number);
                return (
                    <Space direction="vertical" size={4}>
                        {products.map((product, index) => {
                            const offerId = product.offer_id || product?.color?._id;
                            const displayStyle = resolveProductDisplayStyle(offerId);
                            const displayName = productDisplayName(product, nameReplacements);
                            return (
                                <Space key={`${offerId || 'product'}-${index}`} direction="horizontal" size={4}>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<PictureOutlined />}
                                        title="Фото товара"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openStockPreview(offerId);
                                        }}
                                        style={{ paddingInline: 4 }}
                                    />
                                    <Tag
                                        color={tagColor || "green"}
                                        style={{ cursor: 'pointer' }}
                                        title="Изменить остаток"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (!offerId) {
                                                message.warning('Нет артикула товара');
                                                return;
                                            }
                                            setEditingStock({
                                                offerId,
                                                value: Number(product.present) || 0,
                                            });
                                        }}
                                    >
                                        {product.present}
                                    </Tag>
                                    <Tag
                                        color={
                                            displayStyle
                                                ? undefined
                                                : tagColor || product?.color?.ProdColor
                                        }
                                        title={
                                            displayName.original && displayName.original !== displayName.shortened
                                                ? displayName.original
                                                : undefined
                                        }
                                        style={{
                                            width: '100%',
                                            color: 'black',
                                            ...displayStyle,
                                        }}
                                    >
                                        {displayName.label}
                                    </Tag>
                                </Space>
                            );
                        })}
                    </Space>
                );
            },
        },
        {
            title: 'Цвет',
            dataIndex: 'products',
            key: 'color',
            width: 100,
            render: (products, record) => {
                const tagColor = getTagColor(record.posting_number);
                return (
                    <Space direction="vertical" size={4}>
                        {products.map((product, index) => {
                            const offerId = product.offer_id || product?.color?._id;
                            const displayStyle = resolveProductDisplayStyle(offerId);
                            return (
                            <Space key={`color-${product?.color?._id || index}`} direction="horizontal" size={4}>
                                <Tag
                                    color={
                                        displayStyle
                                            ? undefined
                                            : tagColor || product?.color?.ProdColor
                                    }
                                    style={{
                                        width: '100%',
                                        color: 'black',
                                        ...displayStyle,
                                    }}
                                >
                                    {product?.color?.color}
                                </Tag>
                            </Space>
                            );
                        })}
                    </Space>
                );
            },
        },

        {
            title: 'Возвраты',
            dataIndex: 'products',
            key: 'returns',
            width: 110,
            render: renderReturnsCell,
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 150,
            align: 'center',
            render: (_, record) => {
                if (record.status === 'awaiting_packaging') {

                    return (
                        <Button 
                            type="default"
                            onClick={() => handleCollect(record.posting_number)}
                            loading={collectingOrders.has(record.posting_number)}
                            icon={collectingOrders.has(record.posting_number) ? ' ' : <DeliveredProcedureOutlined />}
                        >
                            {/*СОБРАТЬ*/}
                        </Button>
                    );
                }
                if (record.status === 'awaiting_deliver') {
                    const cleanPostingNumber = record.posting_number.replace(/\n/g, '');
                    const isPrinted = orderStatuses.printedOrders.includes(cleanPostingNumber);
                    const rowRemaining = labelTimerRemaining[cleanPostingNumber] || 0;
                    const rowLocked = rowRemaining > 0;
                    const rowCountdown = `${Math.floor(rowRemaining / 60)}:${String(rowRemaining % 60).padStart(2, '0')}`;
                    const printTitle = rowLocked
                        ? `Печать будет доступна через ${rowCountdown}`
                        : undefined;
                    return (
                        <>
                        <Button
                            type={isPrinted ? "primary" : "default"}
                            onClick={() => handlePrint(record.posting_number)}
                            icon={<PrinterOutlined />}
                            disabled={rowLocked}
                            title={printTitle}
                            className={rowLocked ? 'ozon-print-timer-btn' : undefined}
                        >
                            {rowLocked ? rowCountdown : null}
                        </Button>
                    <Button
                        type={"default"}
                        onClick={() => handleRePrint(record.posting_number)}
                        icon={<CiRedo  />}
                        disabled={rowLocked}
                        title={printTitle}
                    >
                        {/*Печать*/}
                    </Button>
                        </>
                    );
                }
                return null;
            }
        },
        {
            title: 'Статус обработки',
            dataIndex: 'posting_number',
            key: 'processing_status',
            width: 200,
            render: (postingNumber) => {
                const statuses = getOrderStatus(postingNumber);
                return getStatusIcons(postingNumber, statuses);
            }
        }

    ];

    const visibleColumns = columns
        .filter(
            (column) =>
                column.key === 'selection' ||
                !tableSettings.hiddenColumns.includes(column.key)
        )
        .map((column) => {
            const width = getColumnWidth(column.key, column.width);
            return {
                ...column,
                width,
                onHeaderCell: () => ({
                    width,
                    onResize: (nextWidth) => updateColumnWidth(column.key, nextWidth, false),
                    onResizeStop: (nextWidth) => updateColumnWidth(column.key, nextWidth, true),
                    onResetWidth: () => resetColumnWidth(column.key),
                }),
            };
        });

    const tableScrollX = visibleColumns.reduce(
        (sum, column) => sum + (Number(column.width) || 80),
        0
    );

    const countArticleInList = (list, articlePattern) => {
        const needle = String(articlePattern || '').trim().toLowerCase();
        if (!needle) return 0;
        let total = 0;
        list.forEach((order) => {
            (order.products || []).forEach((product) => {
                const offerId = getProductOfferId(product).toLowerCase();
                if (offerId.includes(needle)) {
                    total += 1;
                }
            });
        });
        return total;
    };

    const countUncollectedArticleInList = (list, articlePattern) => {
        const needle = String(articlePattern || '').trim().toLowerCase();
        if (!needle) return 0;
        let total = 0;
        list.forEach((order) => {
            const cleanPostingNumber = String(order.posting_number || '').replace(/\n/g, '');
            if (orderStatuses.collectOrders.includes(cleanPostingNumber)) return;
            (order.products || []).forEach((product) => {
                const offerId = getProductOfferId(product).toLowerCase();
                if (offerId.includes(needle)) {
                    total += 1;
                }
            });
        });
        return total;
    };

    const productTypeModules = (() => {
        const groups = new Map();
        productDisplayRules.forEach((rule) => {
            const article = String(rule.article || '').trim();
            if (!article) return;
            const typeLabel = String(rule.label || article).trim() || article;
            const filteredCount = countArticleInList(filteredOrders, article);
            if (filteredCount <= 0) return;
            if (!groups.has(typeLabel)) {
                groups.set(typeLabel, {
                    label: typeLabel,
                    articles: [],
                    items: [],
                });
            }
            const group = groups.get(typeLabel);
            group.articles.push(article);
            group.items.push({
                article,
                count: filteredCount,
                backgroundColor: rule.backgroundColor || '#e6f4ff',
                textColor: rule.textColor || '#000000',
                fontStyle: rule.fontStyle || 'normal',
            });
        });

        return Array.from(groups.values())
            .map((group) => {
                const typeArticles = productDisplayRules
                    .filter(
                        (rule) =>
                            String(rule.label || rule.article || '').trim() === group.label
                    )
                    .map((rule) => String(rule.article || '').trim())
                    .filter(Boolean);
                const totalAll = typeArticles.reduce(
                    (sum, article) => sum + countArticleInList(orders, article),
                    0
                );
                const uncollectedAll = typeArticles.reduce(
                    (sum, article) => sum + countUncollectedArticleInList(orders, article),
                    0
                );
                return {
                    ...group,
                    totalAll,
                    uncollectedAll,
                };
            })
            .filter((group) => group.items.length > 0);
    })();

    useLayoutEffect(() => {
        const wrap = tableWrapRef.current;
        if (!wrap) return undefined;

        const measure = () => {
            const header = wrap.querySelector('.ant-table-header, .ant-table-thead');
            const pagination = wrap.querySelector('.ant-table-pagination, .ant-pagination');
            const wrapHeight = wrap.clientHeight;
            if (!wrapHeight) return;
            const headerHeight = header ? header.getBoundingClientRect().height : 48;
            const paginationHeight = pagination
                ? pagination.getBoundingClientRect().height + 16
                : 64;
            const next = Math.max(200, Math.floor(wrapHeight - headerHeight - paginationHeight));
            setTableScrollY((prev) => (Math.abs(prev - next) > 2 ? next : prev));
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(wrap);
        window.addEventListener('resize', measure);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [
        tableSettings.pinToolbar,
        tableSettings.fontSize,
        showSettingsCard,
        productTypeModules.length,
        loading,
    ]);

    return (
        <div className="p-4 ozon-orders-page">
            <div
                className={`ozon-sticky-toolbar ${
                    tableSettings.pinToolbar ? 'ozon-sticky-toolbar--pinned' : ''
                }`}
            >
            {productTypeModules.length > 0 && (
                <div className="ozon-type-modules mb-4">
                    {productTypeModules.map((module) => (
                        <div key={module.label} className="ozon-type-module">
                            <div className="ozon-type-module__header">
                                <div className="ozon-type-module__title">{module.label}</div>
                                <div className="ozon-type-module__meta">
                                    <span title="Всего без фильтров таблицы">
                                        всего {module.totalAll}
                                    </span>
                                    <span className="ozon-type-module__meta-sep">·</span>
                                    <span title="Не собрано без фильтров таблицы">
                                        не собрано {module.uncollectedAll}
                                    </span>
                                </div>
                            </div>
                            <div className="ozon-type-module__items">
                                {module.items.map((item) => {
                                    const fontStyle = item.fontStyle || 'normal';
                                    return (
                                        <div key={`${module.label}-${item.article}`} className="ozon-type-module__item">
                                            <span
                                                className="ozon-type-module__badge"
                                                style={{
                                                    backgroundColor: item.backgroundColor,
                                                    color: item.textColor,
                                                    fontWeight:
                                                        fontStyle === 'bold' ||
                                                        fontStyle === 'bold-italic'
                                                            ? 700
                                                            : 600,
                                                    fontStyle:
                                                        fontStyle === 'italic' ||
                                                        fontStyle === 'bold-italic'
                                                            ? 'italic'
                                                            : 'normal',
                                                }}
                                            >
                                                {item.count}
                                            </span>
                                            <span className="ozon-type-module__article">{item.article}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="ozon-screen-stats">
                <div className="ozon-screen-stats__item">
                    <div className="ozon-screen-stats__label">Оплачено от OZON</div>
                    <div className="ozon-screen-stats__value">{formatProfitMoney(screenStats.baseSum)}</div>
                </div>
                <div className="ozon-screen-stats__item">
                    <div className="ozon-screen-stats__label">Оплачено покупателем</div>
                    <div className="ozon-screen-stats__value">{formatProfitMoney(screenStats.customerSum)}</div>
                </div>
                <div className="ozon-screen-stats__item">
                    <div className="ozon-screen-stats__label">Чистейшая прибыль</div>
                    <div
                        className="ozon-screen-stats__value"
                        style={{ color: profitTone(screenStats.profitSum) }}
                    >
                        {formatProfitMoney(screenStats.profitSum)}
                    </div>
                </div>
                <div className="ozon-screen-stats__item">
                    <div className="ozon-screen-stats__label">Средняя маржа (база)</div>
                    <div
                        className="ozon-screen-stats__value"
                        style={{
                            color:
                                screenStats.avgMargin === null
                                    ? undefined
                                    : profitTone(screenStats.avgMargin),
                        }}
                    >
                        {screenStats.avgMargin === null ? '—' : formatProfitPercent(screenStats.avgMargin)}
                    </div>
                </div>
                <div className="ozon-screen-stats__item">
                    <div className="ozon-screen-stats__label">Маржа по цене покупки</div>
                    <div
                        className="ozon-screen-stats__value"
                        style={{
                            color:
                                screenStats.realMargin === null
                                    ? undefined
                                    : profitTone(screenStats.realMargin),
                        }}
                    >
                        {screenStats.realMargin === null ? '—' : formatProfitPercent(screenStats.realMargin)}
                    </div>
                </div>
            </div>

            </div>

            {showSettingsCard && (
                <Card className="mb-4" title="Настройки таблицы">
                    <Row gutter={[24, 16]}>
                        <Col xs={24} md={8}>
                            <div className="mb-2 font-medium">Размер шрифта: {tableSettings.fontSize}px</div>
                            <Slider
                                min={10}
                                max={22}
                                step={1}
                                value={tableSettings.fontSize}
                                onChange={(fontSize) => updateTableSettings({ fontSize })}
                                marks={{ 10: '10', 14: '14', 18: '18', 22: '22' }}
                            />
                        </Col>
                        <Col xs={24} md={16}>
                            <div className="mb-2 font-medium">Видимые столбцы</div>
                            <Checkbox.Group
                                style={{ width: '100%' }}
                                value={TABLE_COLUMN_OPTIONS.map((option) => option.key).filter(
                                    (key) => !tableSettings.hiddenColumns.includes(key)
                                )}
                                onChange={(visibleKeys) => {
                                    const hiddenColumns = TABLE_COLUMN_OPTIONS.map(
                                        (option) => option.key
                                    ).filter((key) => !visibleKeys.includes(key));
                                    updateTableSettings({ hiddenColumns });
                                }}
                            >
                                <Row gutter={[8, 8]}>
                                    {TABLE_COLUMN_OPTIONS.map((option) => (
                                        <Col key={option.key} xs={12} sm={8} md={6}>
                                            <Checkbox value={option.key}>{option.label}</Checkbox>
                                        </Col>
                                    ))}
                                </Row>
                            </Checkbox.Group>
                            <div className="mt-4">
                                <Checkbox
                                    checked={tableSettings.highlightPostingDigits !== false}
                                    onChange={(event) =>
                                        updateTableSettings({
                                            highlightPostingDigits: event.target.checked,
                                        })
                                    }
                                >
                                    Выделять чёрным последние 4 цифры номера отправления
                                </Checkbox>
                            </div>
                            <div className="mt-3">
                                <Checkbox
                                    checked={Boolean(tableSettings.pinToolbar)}
                                    onChange={(event) =>
                                        updateTableSettings({
                                            pinToolbar: event.target.checked,
                                        })
                                    }
                                >
                                    Закрепить панели типов при прокрутке
                                </Checkbox>
                            </div>
                        </Col>
                    </Row>
                    <div className="ozon-scanner-settings">
                        <div>
                            <div className="font-medium">Сканер штрихкодов</div>
                            <div className="ozon-scanner-settings__hint">
                                Подключение Bluetooth/COM-сканера через браузер.
                            </div>
                        </div>
                        {scannerStatus === 'unsupported' ? (
                            <div className="ozon-scanner-settings__unsupported">
                                Web Serial API доступен в Chrome и Edge при работе по HTTPS.
                            </div>
                        ) : (
                            <div className="ozon-scanner-settings__controls">
                                <select
                                    className="ozon-scanner-settings__select"
                                    value={tableSettings.scannerPortKey || ''}
                                    disabled={scannerStatus === 'connected'}
                                    onChange={(event) =>
                                        updateTableSettings({
                                            scannerPortKey: event.target.value,
                                        })
                                    }
                                >
                                    <option value="">Выберите COM-порт</option>
                                    {scannerPorts.map((port, index) => {
                                        const key = getSerialPortKey(port, index);
                                        return (
                                            <option key={key} value={key}>
                                                {getSerialPortLabel(port, index)}
                                            </option>
                                        );
                                    })}
                                </select>
                                <Button
                                    type="primary"
                                    onClick={connectScanner}
                                    loading={scannerStatus === 'connecting'}
                                    disabled={scannerStatus === 'connected'}
                                >
                                    Connect
                                </Button>
                                <Button
                                    onClick={() => disconnectScanner()}
                                    disabled={scannerStatus !== 'connected'}
                                >
                                    Disconnect
                                </Button>
                                <Button
                                    type="text"
                                    onClick={() => void refreshScannerPorts()}
                                    disabled={scannerStatus === 'connecting'}
                                >
                                    Обновить список
                                </Button>
                                <span
                                    className={`ozon-scanner-settings__status ozon-scanner-settings__status--${scannerStatus}`}
                                >
                                    {scannerStatus === 'connected'
                                        ? 'Подключён'
                                        : scannerStatus === 'connecting'
                                          ? 'Подключение…'
                                          : 'Отключён'}
                                </span>
                            </div>
                        )}
                        {scannerError && (
                            <div className="ozon-scanner-settings__error">{scannerError}</div>
                        )}
                    </div>
                    <div className="mt-4">
                        <div className="mb-2 text-xs text-gray-500">
                            Ширину колонок можно менять, потянув за правый край заголовка. Двойной
                            клик по краю сбрасывает колонку. Ширины сохраняются в этом браузере.
                        </div>
                        <Button onClick={resetTableSettings}>Сбросить настройки</Button>
                    </div>
                </Card>
            )}

            <Card className="ozon-orders-table-card">
                <div className="ozon-quick-filters">
                    <div className="ozon-quick-filters__main">
                    {[
                        { key: 'all', label: 'Все' },
                        { key: 'active', label: 'Активные' },
                        { key: 'current', label: 'Текущая работа' },
                        { key: 'packaging', label: 'Ожидают Сборки' },
                        { key: 'shipping', label: 'Ожидают Отгрузки' },
                        { key: 'cancelled', label: 'Отменены' },
                        { key: 'delivering', label: 'Доставляются' },
                    ].map((item) => {
                        const active = quickFilter === item.key;
                        const count = quickFilterCounts?.[item.key] ?? 0;
                        return (
                            <button
                                key={item.key}
                                type="button"
                                className={`ozon-quick-filter${active ? ' ozon-quick-filter--active' : ''}`}
                                onClick={() => ozonOrdersStore.setQuickFilter(item.key)}
                            >
                                <span>{item.label}</span>
                                <span className="ozon-quick-filter__count">{count}</span>
                            </button>
                        );
                    })}
                    <span className="ozon-quick-filters__sep" aria-hidden="true" />
                    <Dropdown
                        trigger={['click']}
                        open={typeMenuOpen}
                        onOpenChange={setTypeMenuOpen}
                        popupRender={() => (
                            <div
                                className="ozon-quick-type-menu"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className="ozon-quick-type-menu__section">Сортировка</div>
                                <button
                                    type="button"
                                    className={`ozon-quick-hist-menu__item${
                                        !sortByDisplayType ? ' ozon-quick-hist-menu__item--active' : ''
                                    }`}
                                    onClick={() => setSortByDisplayType(false)}
                                >
                                    По умолчанию
                                </button>
                                <button
                                    type="button"
                                    className={`ozon-quick-hist-menu__item${
                                        sortByDisplayType ? ' ozon-quick-hist-menu__item--active' : ''
                                    }`}
                                    disabled={!productDisplayRules.length}
                                    onClick={() => {
                                        if (!productDisplayRules.length) return;
                                        setSortByDisplayType(true);
                                    }}
                                >
                                    По типу товара
                                </button>
                                <div className="ozon-quick-type-menu__section">Тип товара</div>
                                <div className="ozon-quick-type-menu__tags">
                                    <Tag
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        color={selectedDisplayTypes.length === 0 ? 'blue' : 'default'}
                                        onClick={() => setSelectedDisplayTypes([])}
                                    >
                                        Все
                                    </Tag>
                                    {availableDisplayRules.map((rule) => {
                                        const article = String(rule.article || '').trim();
                                        if (!article) return null;
                                        const selected = selectedDisplayTypes.includes(article);
                                        const fontStyle = rule.fontStyle || 'normal';
                                        return (
                                            <Tag
                                                key={article}
                                                onClick={() => toggleDisplayTypeFilter(article)}
                                                style={{
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    backgroundColor: rule.backgroundColor || undefined,
                                                    color: rule.textColor || undefined,
                                                    borderColor: selected
                                                        ? '#1677ff'
                                                        : rule.backgroundColor || '#d9d9d9',
                                                    outline: selected ? '2px solid #1677ff' : 'none',
                                                    fontWeight:
                                                        fontStyle === 'bold' ||
                                                        fontStyle === 'bold-italic'
                                                            ? 700
                                                            : 400,
                                                    fontStyle:
                                                        fontStyle === 'italic' ||
                                                        fontStyle === 'bold-italic'
                                                            ? 'italic'
                                                            : 'normal',
                                                }}
                                                title={rule.label || article}
                                            >
                                                {article}
                                            </Tag>
                                        );
                                    })}
                                    {!availableDisplayRules.length && (
                                        <span className="ozon-quick-type-menu__empty">
                                            Нет типов в текущей выборке
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    >
                        <button
                            type="button"
                            className={`ozon-quick-filter ozon-quick-hist-btn${
                                sortByDisplayType || selectedDisplayTypes.length
                                    ? ' ozon-quick-filter--active'
                                    : ''
                            }`}
                        >
                            <span>
                                {selectedDisplayTypes.length === 0
                                    ? 'Все типы'
                                    : selectedDisplayTypes.length === 1
                                      ? selectedDisplayTypes[0]
                                      : `Типы (${selectedDisplayTypes.length})`}
                                {sortByDisplayType ? ' · по типу' : ''}
                            </span>
                            <CaretDownOutlined />
                        </button>
                    </Dropdown>
                    <Dropdown
                        trigger={['click']}
                        open={histMenuOpen}
                        onOpenChange={setHistMenuOpen}
                        popupRender={() => (
                            <div
                                className="ozon-quick-hist-menu"
                                onClick={(event) => event.stopPropagation()}
                            >
                                {HIST_PRESETS.map((item) => (
                                    <button
                                        key={item.days}
                                        type="button"
                                        className={`ozon-quick-hist-menu__item${
                                            Number(filters.hist) === item.days
                                                ? ' ozon-quick-hist-menu__item--active'
                                                : ''
                                        }`}
                                        onClick={() => {
                                            ozonOrdersStore.setFilters({ hist: item.days });
                                            setHistMenuOpen(false);
                                        }}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                                <div className="ozon-quick-hist-menu__custom">
                                    <span>Своё</span>
                                    <InputNumber
                                        min={1}
                                        max={365}
                                        size="small"
                                        value={Number(filters.hist) || 3}
                                        onChange={(value) => {
                                            const days = Number(value);
                                            if (!Number.isFinite(days) || days < 1) return;
                                            ozonOrdersStore.setFilters({
                                                hist: Math.min(365, Math.floor(days)),
                                            });
                                        }}
                                    />
                                    <span>дн.</span>
                                </div>
                            </div>
                        )}
                    >
                        <button type="button" className="ozon-quick-filter ozon-quick-hist-btn">
                            <span>{formatHistLabel(filters.hist)}</span>
                            <CaretDownOutlined />
                        </button>
                    </Dropdown>
                    <span className="ozon-quick-filters__sep" aria-hidden="true" />
                    {isLabelPrintLocked && (
                        <span
                            className="ozon-quick-filter ozon-label-timer"
                            title="Ozon готовит этикетки 60 секунд после последней сборки"
                        >
                            <span>Печать через</span>
                            <span className="ozon-quick-filter__count ozon-label-timer__count">
                                {labelPrintCountdown}
                            </span>
                        </span>
                    )}
                    <button
                        type="button"
                        className="ozon-quick-filter"
                        title={
                            isLabelPrintLocked
                                ? `Печать будет доступна через ${labelPrintCountdown}`
                                : 'Скачать маркировки выбранных заказов'
                        }
                        disabled={
                            isLabelPrintLocked ||
                            isLoadingSelected ||
                            ozonOrdersStore.selectedOrders.size === 0
                        }
                        onClick={handleDownloadSelected}
                    >
                        <DownloadOutlined spin={isLoadingSelected} />
                        <span>Выбранные</span>
                        <span className="ozon-quick-filter__count">
                            {ozonOrdersStore.selectedOrders.size}
                        </span>
                    </button>
                    <button
                        type="button"
                        className="ozon-quick-filter"
                        title={
                            isLabelPrintLocked
                                ? `Печать будет доступна через ${labelPrintCountdown}`
                                : 'Скачать маркировки нераспечатанных заказов'
                        }
                        disabled={
                            isLabelPrintLocked ||
                            isLoadingUnprinted ||
                            getUnprintedOrdersCount() === 0
                        }
                        onClick={handleDownloadUnprinted}
                    >
                        <DownloadOutlined spin={isLoadingUnprinted} />
                        <span>Не распечатанные</span>
                        <span className="ozon-quick-filter__count">
                            {getUnprintedOrdersCount()}
                        </span>
                    </button>
                    </div>
                    <div className="ozon-quick-filters__actions">
                        <button
                            type="button"
                            className={`ozon-quick-icon-btn${loading ? ' is-loading' : ''}`}
                            title="Обновить"
                            aria-label="Обновить"
                            disabled={loading}
                            onClick={() => {
                                if (loading) return;
                                message.loading({ content: 'Обновление данных...', key: 'loading' });
                                fetchData();
                            }}
                        >
                            <ReloadOutlined spin={loading} />
                        </button>
                        <button
                            type="button"
                            className={`ozon-quick-icon-btn${v4Test.loading ? ' is-loading' : ''}`}
                            title="Тест: получить отправления через /v4/posting/fbs/list"
                            aria-label="Тест v4 posting list"
                            disabled={v4Test.loading}
                            onClick={handleV4Test}
                        >
                            <ExperimentOutlined spin={v4Test.loading} />
                        </button>
                        <button
                            type="button"
                            className={`ozon-quick-icon-btn${
                                showSettingsCard ? ' ozon-quick-icon-btn--active' : ''
                            }`}
                            title="Настройки таблицы"
                            aria-label="Настройки таблицы"
                            onClick={() => setShowSettingsCard((value) => !value)}
                        >
                            <SettingOutlined />
                        </button>
                        <button
                            type="button"
                            className={`ozon-quick-icon-btn ozon-quick-icon-btn--scanner ozon-quick-icon-btn--scanner-${scannerStatus}`}
                            title={
                                scannerStatus === 'connected'
                                    ? 'Отключить сканер'
                                    : scannerStatus === 'connecting'
                                      ? 'Подключение сканера'
                                      : scannerStatus === 'unsupported'
                                        ? 'Сканер не поддерживается браузером'
                                        : 'Подключить сканер'
                            }
                            aria-label={
                                scannerStatus === 'connected'
                                    ? 'Отключить сканер'
                                    : 'Подключить сканер'
                            }
                            disabled={scannerStatus === 'connecting'}
                            onClick={() => {
                                if (scannerStatus === 'connected') {
                                    void disconnectScanner();
                                } else {
                                    void connectScanner();
                                }
                            }}
                        >
                            <BarcodeOutlined />
                            <span className="ozon-scanner-status-dot" aria-hidden="true" />
                        </button>
                    </div>
                </div>
                <ConfigProvider
                    theme={{
                        components: {
                            Table: {
                                cellFontSize: tableSettings.fontSize,
                                cellFontSizeMD: tableSettings.fontSize,
                                cellFontSizeSM: tableSettings.fontSize,
                                headerFontSize: tableSettings.fontSize,
                            },
                            Tag: {
                                fontSize: tableSettings.fontSize,
                                fontSizeSM: Math.max(10, tableSettings.fontSize - 2),
                            },
                            Button: {
                                contentFontSizeSM: Math.max(10, tableSettings.fontSize - 2),
                                contentFontSize: tableSettings.fontSize,
                            },
                        },
                        token: {
                            fontSize: tableSettings.fontSize,
                        },
                    }}
                >
                    <div
                        ref={tableWrapRef}
                        className="ozon-orders-table-wrap"
                        style={{ fontSize: tableSettings.fontSize }}
                    >
                        <Table
                            columns={visibleColumns}
                            dataSource={filteredOrders}
                            rowKey="order_id"
                            loading={loading}
                            components={{
                                header: {
                                    cell: ResizableTitle,
                                },
                            }}
                            tableLayout="fixed"
                            scroll={{ x: tableScrollX, y: tableScrollY }}
                            pagination={{
                                pageSize: 150,
                                showSizeChanger: true,
                                showTotal: (total) => `Всего ${total} записей`
                            }}
                            rowClassName={(record) => {
                                const cleanPostingNumber = record.posting_number.replace(/\n/g, '');
                                return orderStatuses.collectOrders.includes(cleanPostingNumber) ? 'collected-row' : '';
                            }}
                        />
                    </div>
                </ConfigProvider>
            </Card>

            <OzonPriceDetailsModal
                order={priceDetailsOrder}
                onClose={() => setPriceDetailsOrder(null)}
            />

            <Modal
                open={v4Test.open}
                title="Тест /v4/posting/fbs/list"
                width={1000}
                footer={null}
                onCancel={() => setV4Test((prev) => ({ ...prev, open: false }))}
            >
                {v4Test.loading && <div style={{ padding: 24, textAlign: 'center' }}><Spin /> Загружаем все страницы…</div>}
                {!v4Test.loading && v4Test.error && (
                    <div style={{ color: '#cf1322', whiteSpace: 'pre-wrap' }}>{v4Test.error}</div>
                )}
                {!v4Test.loading && v4Test.data && (
                    <>
                        <div style={{ marginBottom: 12, lineHeight: 1.8 }}>
                            <div>
                                Период: <b>{v4Test.data.since}</b> — <b>{v4Test.data.to}</b> ({v4Test.data.hist} дн.)
                            </div>
                            <div>
                                Отправлений: <b>{v4Test.data.total}</b>, страниц: <b>{v4Test.data.pages}</b>,
                                время: <b>{v4Test.data.elapsed_sec} с</b>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                                {Object.entries(v4Test.data.status_counts || {}).map(([status, count]) => (
                                    <Tag key={status}>{status}: {count}</Tag>
                                ))}
                            </div>
                        </div>
                        <Table
                            size="small"
                            rowKey="posting_number"
                            dataSource={v4Test.data.postings || []}
                            pagination={{ pageSize: 20, showSizeChanger: false }}
                            scroll={{ x: true }}
                            columns={[
                                { title: 'Отправление', dataIndex: 'posting_number', key: 'posting_number' },
                                { title: 'Статус', dataIndex: 'status', key: 'status' },
                                { title: 'Подстатус', dataIndex: 'substatus', key: 'substatus' },
                                {
                                    title: 'Отгрузка',
                                    dataIndex: 'shipment_date',
                                    key: 'shipment_date',
                                    render: (value) => (value ? new Date(value).toLocaleString('ru-RU') : '—'),
                                },
                                {
                                    title: 'Товары',
                                    key: 'products',
                                    render: (_, posting) => (posting.products || []).map((product) => (
                                        <div key={`${product.sku}-${product.offer_id}`}>
                                            {product.offer_id} × {product.quantity}
                                            {product.price?.amount != null ? ` — ${product.price.amount} ${product.price.currency || ''}` : ''}
                                        </div>
                                    )),
                                },
                            ]}
                        />
                        {v4Test.data.postings?.length > 0 && (
                            <details style={{ marginTop: 12 }}>
                                <summary style={{ cursor: 'pointer' }}>Сырой JSON первого отправления</summary>
                                <pre style={{ maxHeight: 360, overflow: 'auto', fontSize: 12, background: '#f6f8fa', padding: 12 }}>
                                    {JSON.stringify(v4Test.data.postings[0], null, 2)}
                                </pre>
                            </details>
                        )}
                    </>
                )}
            </Modal>

            <Modal
                open={stockPreviewOpen}
                title={stockPreview?.article || stockPreview?.offer_id || 'Товар'}
                onCancel={() => {
                    setStockPreviewOpen(false);
                    setStockPreview(null);
                }}
                footer={null}
                width={480}
                destroyOnClose
            >
                {stockPreviewLoading ? (
                    <div style={{ textAlign: 'center', padding: 32 }}>
                        <Spin />
                    </div>
                ) : stockPreview ? (
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <Button
                            type="primary"
                            icon={<LinkOutlined />}
                            block
                            disabled={!stockPreview.sku}
                            onClick={() => {
                                if (!stockPreview.sku) return;
                                window.open(
                                    `https://www.ozon.ru/product/${stockPreview.sku}`,
                                    '_blank',
                                    'noopener,noreferrer'
                                );
                            }}
                        >
                            Открыть на озон
                        </Button>
                        {stockPreview.primary_image ? (
                            <Image
                                src={stockPreview.primary_image}
                                alt={stockPreview.name || stockPreview.article}
                                style={{ width: '100%', maxHeight: 360, objectFit: 'contain' }}
                            />
                        ) : (
                            <Text type="secondary">Нет изображения</Text>
                        )}
                        <div>
                            <Text type="secondary">Артикул</Text>
                            <div>
                                <Text strong>{stockPreview.article || stockPreview.offer_id}</Text>
                            </div>
                        </div>
                        <div>
                            <Text type="secondary">Название</Text>
                            <div>
                                <Text>{stockPreview.name || '—'}</Text>
                            </div>
                        </div>
                        <div>
                            <Text type="secondary">Цена</Text>
                            <div>
                                <Text strong>
                                    {stockPreview.price ? `${stockPreview.price} ₽` : '—'}
                                </Text>
                                {stockPreview.old_price ? (
                                    <Text type="secondary" delete style={{ marginLeft: 8 }}>
                                        {stockPreview.old_price} ₽
                                    </Text>
                                ) : null}
                            </div>
                        </div>
                        <div>
                            <Text type="secondary">Остатки на моих складах</Text>
                            <div>
                                <Text strong>{stockPreview.stocks_my?.present ?? 0}</Text>
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    (резерв {stockPreview.stocks_my?.reserved ?? 0})
                                </Text>
                            </div>
                        </div>
                        <div>
                            <Text type="secondary">Остатки на складах Ozon</Text>
                            <div>
                                <Text strong>{stockPreview.stocks_ozon?.present ?? 0}</Text>
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    (резерв {stockPreview.stocks_ozon?.reserved ?? 0})
                                </Text>
                            </div>
                        </div>
                    </Space>
                ) : null}
            </Modal>

            <Modal
                open={Boolean(editingStock)}
                title={`Остаток: ${editingStock?.offerId || ''}`}
                onCancel={() => {
                    if (!savingStock) setEditingStock(null);
                }}
                okText="Сохранить"
                cancelText="Отмена"
                confirmLoading={savingStock}
                onOk={saveStockQuantity}
                destroyOnClose
                width={360}
            >
                <InputNumber
                    autoFocus
                    min={0}
                    precision={0}
                    style={{ width: '100%' }}
                    value={editingStock?.value}
                    onChange={(value) =>
                        setEditingStock((prev) =>
                            prev ? { ...prev, value: value ?? 0 } : prev
                        )
                    }
                    onPressEnter={saveStockQuantity}
                    disabled={savingStock}
                />
            </Modal>
        </div>
    );
});

export default OzoneOrders;